/**
 * GenerateSourceRow - Horizontal scrollable row of pool thumbnails.
 * User clicks a thumbnail to select it as the source image for generation.
 */

import { ImageIcon } from 'lucide-react';
import type { AssetPoolItem } from '@/lib/api/products';

interface GenerateSourceRowProps {
  assets: AssetPoolItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function GenerateSourceRow({ assets, selectedId, onSelect }: GenerateSourceRowProps) {
  if (assets.length === 0) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-neutral-500 dark:text-neutral-400">
        <ImageIcon className="h-4 w-4" />
        Keine Quellbilder im Pool vorhanden.
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
        Quellbild waehlen
      </label>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {assets.map((asset) => (
          <SourceThumb
            key={asset.id}
            asset={asset}
            isSelected={asset.id === selectedId}
            onSelect={() => onSelect(asset.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Thumb sub-component (inline, tiny)
// ============================================================================

interface SourceThumbProps {
  asset: AssetPoolItem;
  isSelected: boolean;
  onSelect: () => void;
}

function SourceThumb({ asset, isSelected, onSelect }: SourceThumbProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Quellbild ${asset.id} waehlen`}
      className={`shrink-0 h-16 w-16 rounded-md overflow-hidden border-2 transition-all ${
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-500/30'
          : 'border-neutral-200 dark:border-neutral-700 hover:border-primary-300'
      }`}
    >
      <img
        src={asset.thumbnail_url || ''}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
      />
    </button>
  );
}
